import { ESTIMATED_KIND_KEY, type LedgerCost, type PricedHoursLedger } from "@torre/work-hours";

// ---------------------------------------------------------------------------
// El libro en la pantalla (#1344). `GET /workHoursDetail` trae las líneas que
// suman cada cifra del tablero; aquí se agrupan para cada acordeón (por tipo,
// por repositorio, por PR, cada renglón de la cuenta de la diferencia) y se
// suma su costo. Funciones puras: las tablas solo pintan.
//
// Dinero: cada línea trae su costo en la moneda de la persona y su equivalente
// en MXN. Un total se muestra en MXN si TODAS sus líneas lo tienen; si falta un
// tipo de cambio, se muestra por moneda (nunca se suman monedas distintas).
// Una persona sin sueldo registrado deja el total marcado como incompleto.
// ---------------------------------------------------------------------------

export type Ledger = PricedHoursLedger;
export type EntryLine = Ledger["entries"][number];
export type ActivityLine = Ledger["activity"][number];
export type PullRequestLine = Ledger["pullRequests"][number];

/** Minutos → horas con 2 decimales, sin ceros de relleno (152 → «2.53 h»). */
export const hoursOf = (minutes: number): string => `${Math.round((minutes / 60) * 100) / 100} h`;

export interface Money {
  byCurrency: Record<string, number>;
  /** Equivalente en MXN, o `null` si a alguna línea le falta tipo de cambio. */
  mxn: number | null;
  /** Alguna línea es de una persona sin sueldo registrado: el total queda corto. */
  incomplete: boolean;
  /** No hay ninguna línea con importe. */
  empty: boolean;
}

/** Suma costos de líneas. `undefined` = la línea no trae costo (quien mira no es dueño). */
export function sumCost(costs: (LedgerCost | null | undefined)[]): Money {
  const byCurrency: Record<string, number> = {};
  let mxn: number | null = 0;
  let incomplete = false;
  let priced = 0;
  for (const c of costs) {
    if (c === undefined) continue;
    if (c === null) {
      incomplete = true;
      continue;
    }
    priced += 1;
    byCurrency[c.currency] = (byCurrency[c.currency] ?? 0) + c.amount;
    if (mxn !== null) mxn = c.mxn === null ? null : mxn + c.mxn;
  }
  for (const k of Object.keys(byCurrency)) byCurrency[k] = Math.round(byCurrency[k] * 100) / 100;
  return { byCurrency, mxn: mxn === null ? null : Math.round(mxn * 100) / 100, incomplete, empty: priced === 0 };
}

/** Suma totales ya calculados (p. ej. el de cada PR listado de un repo). */
export function sumMoney(list: Money[]): Money {
  const byCurrency: Record<string, number> = {};
  let mxn: number | null = 0;
  let incomplete = false;
  let empty = true;
  for (const m of list) {
    incomplete = incomplete || m.incomplete;
    if (m.empty) continue;
    empty = false;
    for (const [c, v] of Object.entries(m.byCurrency)) byCurrency[c] = Math.round(((byCurrency[c] ?? 0) + v) * 100) / 100;
    if (mxn !== null) mxn = m.mxn === null ? null : Math.round((mxn + m.mxn) * 100) / 100;
  }
  return { byCurrency, mxn: empty ? 0 : mxn, incomplete, empty };
}

/** Tipo de cambio a MXN por moneda, como lo manda el API (`expected.cost.fx`). */
export type FxRates = Record<string, { rate: number; date?: string | null; source?: string | null }>;

/** Equivalente en MXN de un importe; `null` si su moneda no tiene tipo de cambio. */
export function toMxn(amount: number, currency: string, fx: FxRates | null | undefined): number | null {
  if (currency === "MXN") return amount;
  const rate = Number(fx?.[currency]?.rate);
  return rate > 0 ? Math.round(amount * rate * 100) / 100 : null;
}

/**
 * Importes por moneda (`{ MXN: 1200, USD: 50 }`) → un total con su equivalente
 * en MXN. Las monedas en cero no cuentan: no hay nada que convertir.
 */
export function moneyOfAmounts(byCurrency: Record<string, number>, fx: FxRates | null | undefined, incomplete = false): Money {
  const out: Record<string, number> = {};
  let mxn: number | null = 0;
  for (const [currency, value] of Object.entries(byCurrency)) {
    const amount = Math.round(value * 100) / 100;
    if (!amount) continue;
    out[currency] = amount;
    const converted = toMxn(amount, currency, fx);
    if (mxn !== null) mxn = converted === null ? null : Math.round((mxn + converted) * 100) / 100;
  }
  return { byCurrency: out, mxn, incomplete, empty: Object.keys(out).length === 0 };
}

/** Lo que un total trae en moneda distinta de MXN («$96.00 USD»); `null` si todo es MXN. */
export function foreignLabel(m: Money, language = "es"): string | null {
  const codes = Object.keys(m.byCurrency).filter((c) => c !== "MXN" && m.byCurrency[c] !== 0).sort();
  return codes.length ? codes.map((c) => fmt(m.byCurrency[c], c, language)).join(" · ") : null;
}

const fmt = (value: number, currency: string, language: string) =>
  `${new Intl.NumberFormat(language.startsWith("en") ? "en-US" : "es-MX", { style: "currency", currency, currencyDisplay: "narrowSymbol", maximumFractionDigits: 2 }).format(value)} ${currency}`;

/** «$1,234.50 MXN»; por moneda si falta tipo de cambio; «—» si no hay importe. */
export function moneyLabel(m: Money, language = "es"): string {
  if (m.empty) return "—";
  if (m.mxn !== null) return fmt(m.mxn, "MXN", language);
  return Object.keys(m.byCurrency)
    .sort()
    .map((c) => fmt(m.byCurrency[c], c, language))
    .join(" · ");
}

/** Importe en la moneda original cuando no es MXN (para el detalle de una línea). */
export function nativeLabel(c: LedgerCost | null | undefined, language = "es"): string | null {
  return c && c.currency !== "MXN" ? fmt(c.amount, c.currency, language) : null;
}

// --- Lo que cuenta, línea por línea ------------------------------------------------

export type CountedOrigin = "manual" | "commits" | "crm" | "estimate" | "support";

/** Una línea de lo que CUENTA en el total: una entrada, o el estimado de un día sin entradas. */
export interface CountedLine {
  key: string;
  userId: string;
  userName: string | null;
  date: string;
  repo: string;
  /** `ESTIMATED_KIND_KEY` para los días sin registro. */
  kind: string;
  origin: CountedOrigin;
  note: string | null;
  minutes: number;
  createdAt: string | null;
  commitCount: number;
  otherRepo: boolean;
  cost: LedgerCost | null | undefined;
}

export const originOfEntry = (e: EntryLine): CountedOrigin => (e.source === "crm" ? "crm" : e.commits.length > 0 ? "commits" : "manual");

export function countedLines(ledger: Ledger): CountedLine[] {
  const out: CountedLine[] = [];
  ledger.entries.forEach((e, i) =>
    out.push({
      key: `e:${e.id ?? i}`,
      userId: e.userId,
      userName: e.userName,
      date: e.date,
      repo: e.repo,
      kind: e.kind,
      origin: originOfEntry(e),
      note: e.note,
      minutes: e.minutes,
      createdAt: e.createdAt,
      commitCount: e.commits.length,
      otherRepo: e.otherRepo,
      cost: e.cost,
    }),
  );
  for (const a of ledger.activity) {
    if (!a.estimatedDay || a.minutes <= 0) continue;
    out.push({
      key: `a:${a.userId}|${a.date}|${a.repo}`,
      userId: a.userId,
      userName: a.userName,
      date: a.date,
      repo: a.repo,
      kind: ESTIMATED_KIND_KEY,
      origin: "estimate",
      note: null,
      minutes: a.minutes,
      createdAt: null,
      commitCount: a.commitCount,
      otherRepo: false,
      cost: a.cost,
    });
  }
  for (const s of ledger.support) {
    out.push({
      key: `s:${s.userId}|${s.date}`,
      userId: s.userId,
      userName: s.userName,
      date: s.date,
      repo: "",
      kind: ESTIMATED_KIND_KEY,
      origin: "support",
      note: null,
      minutes: s.minutes,
      createdAt: null,
      commitCount: 0,
      otherRepo: false,
      cost: s.cost,
    });
  }
  return out;
}

export interface Group<T> {
  key: string;
  minutes: number;
  money: Money;
  lines: T[];
}

/** Agrupa líneas y suma minutos y costo de cada grupo (orden: más minutos primero). */
export function groupLines<T extends { minutes: number; cost?: LedgerCost | null }>(lines: T[], keyOf: (l: T) => string): Group<T>[] {
  const map = new Map<string, T[]>();
  for (const l of lines) map.set(keyOf(l), [...(map.get(keyOf(l)) ?? []), l]);
  return [...map.entries()]
    .map(([key, list]) => ({ key, minutes: list.reduce((n, l) => n + l.minutes, 0), money: sumCost(list.map((l) => l.cost)), lines: list }))
    .sort((a, b) => b.minutes - a.minutes || a.key.localeCompare(b.key));
}

/** Subtotal por persona de un conjunto de líneas. */
export interface PersonSubtotal {
  userId: string;
  userName: string | null;
  minutes: number;
  lines: number;
  money: Money;
}

export function byPerson<T extends { userId: string; userName: string | null; minutes: number; cost?: LedgerCost | null }>(lines: T[]): PersonSubtotal[] {
  return groupLines(lines, (l) => l.userId).map((g) => ({ userId: g.key, userName: g.lines[0].userName, minutes: g.minutes, lines: g.lines.length, money: g.money }));
}

// --- PRs por repositorio -----------------------------------------------------------

export interface PullRequestGroup {
  key: string;
  repo: string;
  number: number;
  minutes: number;
  money: Money;
  people: string[];
}

export interface RepoPullRequests {
  repo: string;
  minutes: number;
  money: Money;
  pullRequests: PullRequestGroup[];
}

export function pullRequestsByRepo(ledger: Ledger): RepoPullRequests[] {
  return groupLines(ledger.pullRequests, (l) => l.repo).map((r) => ({
    repo: r.key,
    minutes: r.minutes,
    money: r.money,
    pullRequests: groupLines(r.lines, (l) => l.key).map((p) => ({
      key: p.key,
      repo: r.key,
      number: Number(p.key.slice(p.key.lastIndexOf("#") + 1)) || 0,
      minutes: p.minutes,
      money: p.money,
      people: [...new Set(p.lines.map((l) => l.userName ?? l.userId))].sort(),
    })),
  }));
}

// --- La cuenta de la diferencia, renglón por renglón --------------------------------

export interface BridgeDetail {
  /** Toda la actividad por commits (lo que suman «Por repositorio» y los PRs). */
  activity: ActivityLine[];
  /** Entradas que no citan commits. */
  withoutCommits: EntryLine[];
  /** Persona/día/repo donde lo registrado con commits supera lo que demuestran. */
  beyond: ActivityLine[];
  /** Persona/día/repo con commits y menos horas registradas que su actividad. */
  commitsWithoutHours: ActivityLine[];
  support: Ledger["support"];
  otherRepos: EntryLine[];
}

export function bridgeDetail(ledger: Ledger): BridgeDetail {
  return {
    activity: ledger.activity.filter((a) => a.minutes > 0),
    withoutCommits: ledger.entries.filter((e) => !e.otherRepo && e.commits.length === 0),
    beyond: ledger.activity.filter((a) => a.beyondMinutes > 0).sort((a, b) => b.beyondMinutes - a.beyondMinutes),
    commitsWithoutHours: ledger.activity.filter((a) => a.commitsWithoutHoursMinutes > 0).sort((a, b) => b.commitsWithoutHoursMinutes - a.commitsWithoutHoursMinutes),
    support: ledger.support,
    otherRepos: ledger.entries.filter((e) => e.otherRepo),
  };
}

/** Entradas con commits de la misma persona, día y repo que una línea de actividad. */
export const entriesOfActivity = (ledger: Ledger, a: ActivityLine): EntryLine[] =>
  ledger.entries.filter((e) => !e.otherRepo && e.commits.length > 0 && e.userId === a.userId && e.date === a.date && e.repo === a.repo);

/** Hora local (America/Mexico_City) de un instante ISO: «09:30». */
export function localTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
}
